import React, { useEffect, useState, useMemo, useContext } from "react";
import { formatDateOnly } from "helpers/dateOnly";
import { useNavigate } from "react-router-dom";
import H5 from "components/typography/H5";
import { Box, Flex, Grid, Spinner, Link } from "theme-ui";
import { themevals } from "theme/themevals";
import H2 from "components/typography/H2";
import P from "components/typography/P";
import { ShareIcon, DownloadIcon, PlaneIcon, CheckMarkIcon, TimesIcon, QuestionMarkIcon } from "components/svg";
import moment from "moment-timezone";
import { createColumnHelper } from "@tanstack/react-table";
import TableBasic from "components/ui/Table";
import { useToast } from "components/toast";
import { getPoPDF, emailPo, updatePurchaseOrder } from "api/PurchaseOrder";
import { setPurchaseOrderSurcharge, FUEL_SURCHARGE_TYPE } from "api/FuelSurcharge";
import { getMapSnapshots } from "api/MulchMap";
import SnapshotIncludePicker from "components/SnapshotIncludePicker";
import TermsEditor from "components/TermsEditor";
import { createNote } from "api/Notes";
import BasicButton from "components/forms/BasicButton";
import UserContext from "context/User";
import BasicModal from "components/ui/BasicModal";
import { acceptOrDeny } from "api/PurchaseOrder";
import { trackActivity } from "api/ActivityTracker";
import PaymentTracker from "components/figmaUI/PaymentTracker";

const Overview = ({ isCompany, po, reloadPO, company, onRequireAcknowledgment, termsAcknowledgedAt, acceptAfterAck }: { isCompany: boolean; po: any; company: any; reloadPO: (po: any) => void; onRequireAcknowledgment?: () => void; termsAcknowledgedAt?: string | null; acceptAfterAck?: number }) => {
  const [sendSubmitting, setSendSubmitting] = useState<boolean>(false);
  // Session 28 hotfix 6 (Phase 3): snapshot inclusion is now persisted
  // on `purchase_order.mulchMapSnapshotIds`. The picker component
  // handles the selection UI; frontend just tells the picker how many
  // exist.
  // Session 28 hotfix 11: `user` moved up so the snapshot-count
  // effect below can reference it. Original declaration site is
  // removed to avoid a duplicate.
  const { user } = useContext<any>(UserContext);

  const [snapshotCount, setSnapshotCount] = useState<number>(0);

  // Session 28 hotfix 11: gated on company access. `getMapSnapshots`
  // is company-only; providers viewing a PO would 403 and get bounced
  // by the global interceptor. The picker chip is already company-only
  // in the JSX.
  useEffect(() => {
    if (!user || user.accessLevel !== "isCompany") {
      setSnapshotCount(0);
      return;
    }
    const mulchMapId = po && po.mulchMapId;
    if (!mulchMapId) {
      setSnapshotCount(0);
      return;
    }
    let cancelled = false;
    (async () => {
      const snapshots = await getMapSnapshots(mulchMapId);
      if (cancelled) return;
      const count = Array.isArray(snapshots) ? snapshots.length : 0;
      setSnapshotCount(count);
    })();
    return () => { cancelled = true; };
  }, [user && user.accessLevel, po && po.mulchMapId]);
  const [downloadSubmitting, setDownloadSubmitting] = useState<boolean>(false);
  const [confirmAcceptModalOpen, setConfirmAcceptModalOpen] = useState<boolean>(false);
  const [acceptNoteModalOpen, setAcceptNoteModalOpen] = useState<boolean>(false);
  const [confirmDenyModalOpen, setConfirmDenyModalOpen] = useState<boolean>(false);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [acceptNote, setAcceptNote] = useState<string>("");
  const [denyNote, setDenyNote] = useState<string>("");
  const [items, setItems] = useState<any[]>([]);
  const navigate = useNavigate();
  const columnHelper = createColumnHelper();
  const toast = useToast();
  // Provider-side fuel surcharge (after acceptance): refresh / remove, reason required.
  const [fuelAction, setFuelAction] = useState<{ mode: "refresh" | "remove"; reason: string; busy: boolean } | null>(null);

  useEffect(() => {
    if (po) {
      const flatItems = po.lineitems?.flat() || [];
      setItems(flatItems);
    }
  }, [po]);

  // Get provider from the first line item's depot (correct versioned provider)
  const getProviderInfo = () => {
    if (items && items.length > 0 && items[0]?.depot?.provider) {
      return {
        name: items[0].depot.provider.name,
        contactEmail: items[0].depot.provider.contactEmail
      };
    }
    // Fallback to root provider if line items don't have provider info
    return {
      name: po.provider?.name || null,
      contactEmail: po.provider?.contactEmail || null
    };
  };

  const providerInfo = getProviderInfo();

  // Helper to check if project is complete (for showing payment tracker)
  // Show payment tracker when project status is Complete, Closed, or Invoice Sent
  // Show payment tracker only when PO is accepted (status >= 2) AND project is in a relevant state.
  const showPaymentTracker = po.status >= 2 && ['Complete', 'Closed', 'Invoice Sent'].includes(po.project?.status?.name)

  // Helper to check if the client payment is late
  const isClientPaymentLate = () => {
    // Only check if invoice sent but client hasn't paid yet
    const invoiceSent = po.invoice?.status >= 1;
    const depositedPayment = po.invoice?.payments?.find((p: any) => p.deposited === true);
    
    if (!invoiceSent || depositedPayment || po.paymentStatus === 2) {
      return false;
    }
    
    // Check if we have a due date on the invoice
    if (po.invoice?.dueDate) {
      const dueDate = moment(po.invoice.dueDate);
      const today = moment();
      return today.isAfter(dueDate);
    }
    
    return false;
  };

  // Helper to determine current payment stage
  const getCurrentPaymentStage = () => {
    // Check if invoice has been sent (status 1 = Sent, 2 = Paid, 3 = Partial Paid)
    const invoiceSent = po.invoice?.status >= 1;
    
    // Check if client has paid (invoice has a deposited payment)
    const depositedPayment = po.invoice?.payments?.find((p: any) => p.deposited === true);
    
    // Check if PO payment status is marked as Paid (paymentStatus === 2)
    if (po.paymentStatus === 2) return "paid";
    if (depositedPayment) return "client_paid";
    if (invoiceSent) return "invoice_sent";
    return "not_started";
  };
  
  // Get the deposited payment date for display
  const getDepositedPaymentDate = () => {
    const depositedPayment = po.invoice?.payments?.find((p: any) => p.deposited === true);
    if (depositedPayment) {
      // Use depositedDate if available, otherwise fall back to transactionDate
      const dateToUse = depositedPayment.depositedDate || depositedPayment.transactionDate;
      return dateToUse ? moment(dateToUse).format("MMM D, YYYY") : undefined;
    }
    return undefined;
  };

  // Get the paid date when PO is marked as paid (paymentStatus === 2)
  const getPaidTransactionDate = () => {
    // When paymentStatus is 2 (Paid), look for the purchase order payment transaction date
    if (po.paymentStatus === 2) {
      // Look for the most recent purchase order payment with a transaction date
      const payments = po.payments || [];
      const paidPayment = payments.find((p: any) => p.transactionDate);
      if (paidPayment?.transactionDate) {
        return moment(paidPayment.transactionDate).format("MMM D, YYYY");
      }
    }
    return undefined;
  };

  const handleCopyLink = () => {
    navigator.clipboard.writeText(window.location.href);
    toast.add("Link copied to clipboard.", "var(--theme-ui-colors-success)");
  };

  const handleEmailPO = async () => {
    setSendSubmitting(true);
    // Session 28 hotfix 6: backend reads purchase_order.mulchMapSnapshotIds.
    const res = await emailPo({ data: { id: po.id } });
    if (res.success) {
      toast.add("PO emailed successfully.", "var(--theme-ui-colors-success)");
      void trackActivity({
        module: "purchase_orders",
        action: "send_purchase_order",
        actionType: "create",
        userId: user?.id || null,
        companyId: user?.company?.id || company?.id || null,
        entityType: "purchase_order",
        entityId: po.id,
        metadata: {
          providerId: po.provider?.id || null,
          providerEmail: po.provider?.contactEmail || null,
        },
        userAgent: typeof navigator !== "undefined" ? navigator.userAgent : null,
      });
    } else {
      toast.add("Failed to email PO.", "var(--theme-ui-colors-danger)");
    }
    reloadPO(po.id);
    setSendSubmitting(false);
  };

  const handleDownloadPO = async () => {
    setDownloadSubmitting(true);
    // Session 28 hotfix 6: backend reads purchase_order.mulchMapSnapshotIds.
    const res = await getPoPDF({ data: { id: po.id } });

    void trackActivity({
      module: "purchase_orders",
      action: "generate_purchase_order_pdf",
      actionType: "create",
      userId: user?.id || null,
      companyId: user?.company?.id || null,
      entityType: "purchase_order",
      entityId: po.id,
      metadata: {},
      userAgent: typeof navigator !== "undefined" ? navigator.userAgent : null,
    });

    const link = document.createElement("a");
    link.href = res.result;
    link.download = `GMO_${String(po.sequence).padStart(5, "0")}_PurchaseOrder.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setDownloadSubmitting(false);
  };

  const columns = [
    columnHelper.accessor((row: any) => row?.name, {
      id: "itemName",
      cell: (info: any) => <Box sx={{ color: "#000", fontWeight: "500" }}>{info.getValue()}</Box>,
      header: () => <span>Item</span>,
      enableSorting: false,
      meta: {
        style: { width: "200px", minWidth: "150px" },
      },
    }),
    columnHelper.accessor((row: any) => row?.description, {
      id: "description",
      cell: (info: any) => <Box sx={{ color: themevals.colors.dkgrey, fontWeight: "500" }}>{info.getValue()}</Box>,
      header: () => <span>Description</span>,
      enableSorting: false,
    }),
    columnHelper.accessor((row: any) => row?.quantity, {
      id: "quantity",
      cell: (info: any) => {
        const row = info.row.original;
        const showUnit = row.type !== "service"; // Only show unit if not service

        return (
          <Box sx={{ color: themevals.colors.dkgrey, fontWeight: "500" }}>
            {info.getValue()} {showUnit && row.unit}
          </Box>
        );
      },
      header: () => <span>Qty</span>,
      enableSorting: false,
    }),
    columnHelper.accessor((row: any) => row?.cost, {
      id: "rate",
      cell: (info: any) => {
        const value = info.getValue();
        return <Box sx={{ color: themevals.colors.dkgrey, fontWeight: "500" }}>{value?.toLocaleString("en-US", { style: "currency", currency: "USD" })}</Box>;
      },
      header: () => <span>Rate</span>,
      enableSorting: false,
    }),
    columnHelper.accessor((row: any) => row?.cost * row?.quantity, {
      id: "total",
      cell: (info: any) => {
        const value = info.getValue();
        return <Box sx={{ color: themevals.colors.dkgrey, fontWeight: "500", textAlign: "right" }}>{value?.toLocaleString("en-US", { style: "currency", currency: "USD" })}</Box>;
      },
      header: () => (
        <Flex sx={{ width: "100%", alignItems: "center", justifyContent: "flex-end" }}>
          <Box>Amount</Box>
        </Flex>
      ),
      enableSorting: false,
    }),
  ];

  const cancelAcceptConfirm = () => {
    setConfirmAcceptModalOpen(false);
    setAcceptNote(""); // Clear note if they cancel
  };

  const cancelDenyConfirm = () => {
    setConfirmDenyModalOpen(false);
    setDenyNote(""); // Clear note if they cancel
  };

  const toggleAcceptModal = () => {
    // Session 76 — order of operations. If the terms have not been
    // acknowledged, the server will refuse this acceptance with a 409,
    // so asking "Are you sure?" first is asking someone to confirm
    // something we are about to reject. Go straight to the
    // acknowledgment modal instead; the confirm step is for the case
    // where acceptance can actually proceed.
    // Either source counts: `po` carries it on a PO acknowledged in an
    // earlier session, the prop carries it when it was acknowledged in
    // this one.
    const acknowledged = po?.termsAcknowledgedAt || termsAcknowledgedAt;
    if (!confirmAcceptModalOpen && !acknowledged) {
      if (typeof onRequireAcknowledgment === "function") {
        onRequireAcknowledgment();
        return;
      }
    }
    setConfirmAcceptModalOpen(!confirmAcceptModalOpen);
  };

  // Acknowledging the terms (in this session) was the provider's "accept"
  // click: carry on to the accept confirmation instead of leaving the PO on
  // Sent (2026-10-20, Jack: acknowledged, saw a success toast, PO still Sent).
  useEffect(() => {
    if (acceptAfterAck && Number(po?.status) === 1) setConfirmAcceptModalOpen(true);
  }, [acceptAfterAck]); // eslint-disable-line

  const toggleDenyModal = () => {
    setConfirmDenyModalOpen(!confirmDenyModalOpen);
  };

  const handleAcceptConfirm = async () => {
    // Accept the PO immediately
    setSubmitting(true);
    setConfirmAcceptModalOpen(false);
    
    try {
      const res = await acceptOrDeny({ data: { id: po.id }, update: { status: 2 } });
      
      // §3.9 #11 — backend gates Sent → Accepted on
      // termsAcknowledgedAt. If the provider dismissed the modal
      // via Reject and then tries Accept anyway, we get 409 back.
      // Detect and re-prompt via the parent's callback.
      if (res?.requiresAcknowledgment || res?.message === "Acknowledgment required") {
        setSubmitting(false);
        toast.add("Please acknowledge the terms before accepting.", "var(--theme-ui-colors-info)");
        if (typeof onRequireAcknowledgment === "function") onRequireAcknowledgment();
        return;
      }
      
      if (res.po) {
        void trackActivity({
          module: "purchase_orders",
          action: "accept_purchase_order",
          actionType: "update",
          userId: user?.id || null,
          companyId: user?.company?.id || null,
          entityType: "purchase_order",
          entityId: res.po.id,
          metadata: {},
          userAgent: typeof navigator !== "undefined" ? navigator.userAgent : null,
        });
        
        reloadPO(res.po);
        
        // Show the note modal after accepting
        setSubmitting(false);
        setAcceptNoteModalOpen(true);
      } else {
        // Any server error used to fail silently here (2026-10-20).
        setSubmitting(false);
        const msg = Array.isArray(res?.message) ? res.message.join(", ") : res?.message || res?.error;
        toast.add(`Could not accept the PO${msg ? `: ${msg}` : "."}`, "var(--theme-ui-colors-danger)");
      }
    } catch (error) {
      console.error("Error accepting PO:", error);
      toast.add("Failed to accept purchase order.", "var(--theme-ui-colors-danger)");
      setSubmitting(false);
    }
  };

  // After accepting, a provider's work happens on the project (2026-10-20).
  const goToProjectIfProvider = () => {
    if (!isCompany && po?.project?.id) navigate(`/projects/${po.project.id}`);
  };

  const handleCancelAcceptNote = () => {
    setAcceptNoteModalOpen(false);
    setAcceptNote("");
    toast.add("PO accepted.", "var(--theme-ui-colors-success)");
    goToProjectIfProvider();
  };

  const handleSaveAcceptNote = async () => {
    setSubmitting(true);
    
    try {
      // Save note if provided
      if (acceptNote && acceptNote.trim()) {
        const noteData = {
          note: acceptNote.trim(),
          entityId: po.id,
          entityType: "PurchaseOrder",
          private: false,
        };
        await createNote({ data: noteData });
      }
      
      setAcceptNote("");
      setAcceptNoteModalOpen(false);
      toast.add("PO accepted.", "var(--theme-ui-colors-success)");
      goToProjectIfProvider();
    } catch (error) {
      console.error("Error saving note:", error);
      toast.add("PO accepted, but failed to save note.", "var(--theme-ui-colors-warning)");
      setAcceptNoteModalOpen(false);
    } finally {
      setSubmitting(false);
    }
  };

  const submitDeny = async () => {
    setSubmitting(true);
    
    try {
      // Deny the PO
      const res = await acceptOrDeny({ data: { id: po.id }, update: { status: 4 } });
      
      if (res.po) {
        // If there's a note, create it
        if (denyNote && denyNote.trim()) {
          const noteData = {
            note: denyNote.trim(),
            entityId: po.id,
            entityType: "PurchaseOrder",
            private: false,
          };
          await createNote({ data: noteData });
        }
        
        toast.add("PO denied.", "var(--theme-ui-colors-danger)");
        void trackActivity({
          module: "purchase_orders",
          action: "deny_purchase_order",
          actionType: "update",
          userId: user?.id || null,
          companyId: user?.company?.id || null,
          entityType: "purchase_order",
          entityId: res.po.id,
          metadata: { note: denyNote },
          userAgent: typeof navigator !== "undefined" ? navigator.userAgent : null,
        });
        setDenyNote("");
        
        reloadPO(res.po);
      }
    } catch (error) {
      console.error("Error denying PO:", error);
      toast.add("Failed to deny purchase order.", "var(--theme-ui-colors-danger)");
    } finally {
      setSubmitting(false);
      setConfirmDenyModalOpen(false);
    }
  };

  return (
    <>
      <H5 fontFamily="Rubik" fontSize="16px" marginLeft="0px" marginTop="0px">
        Overview
      </H5>
      <Flex sx={{ maxWidth: "1000px", width: "100%", margin: "0 auto 20px auto", alignItems: "flex-end", gap: "40px" }}>
        <Flex sx={{ gap: "20px" }}>
          <Box sx={{ cursor: "pointer" }} onClick={() => handleCopyLink()}>
            <ShareIcon fill={themevals.colors.dkgrey} width="20px" />
          </Box>
          {isCompany && (
            <Box sx={{ cursor: "pointer" }} onClick={handleEmailPO}>
              {sendSubmitting && <Spinner size={20} color={themevals.colors.primary} />}
              {!sendSubmitting && <PlaneIcon fill={themevals.colors.dkgrey} width="20px" />}
            </Box>
          )}
          <Box sx={{ cursor: "pointer" }} onClick={() => handleDownloadPO()}>
            {downloadSubmitting && <Spinner size={20} color={themevals.colors.primary} />}
            {!downloadSubmitting && <DownloadIcon fill={themevals.colors.dkgrey} width="20px" />}
          </Box>
          {snapshotCount > 0 && isCompany && po?.id && po?.mulchMapId && (
            <SnapshotIncludePicker
              recordType="purchase_order"
              recordId={po.id}
              mulchMapId={po.mulchMapId}
              initialSelectedIds={
                Array.isArray(po.mulchMapSnapshotIds)
                  ? po.mulchMapSnapshotIds
                  : []
              }
            />
          )}
        </Flex>
        <Flex sx={{ justifyContent: "flex-end", flex: 1, gap: "20px", alignItems: "center", marginLeft: "20px", flexWrap: "wrap" }}>
          {/* Accept/Deny buttons — show when PO is Sent (status 1), regardless of other state */}
          {po.status === 1 && (
            <>
              <BasicButton scheme="dangerSecondary" type="button" onClick={toggleDenyModal}>
                <TimesIcon fill={themevals.colors.danger} /> Deny
              </BasicButton>
              <BasicButton type="button" $iconHeight="14px" $iconWidth="14px" $loadingColor="#fff" keepText={true} onClick={toggleAcceptModal}>
                <CheckMarkIcon fill={"#fff"} />
                Accept
              </BasicButton>
            </>
          )}
          {/* Payment tracker — show when PO accepted and project in relevant state */}
          {showPaymentTracker && (
            <PaymentTracker
              stages={[
                {
                  label: "Invoice Sent to Client",
                  date: po.invoice?.sentDate ? moment(po.invoice.sentDate).format("MMM D, YYYY") : undefined,
                  isCompleted: ["invoice_sent", "client_paid", "paid"].includes(getCurrentPaymentStage()),
                  isActive: getCurrentPaymentStage() === "invoice_sent",
                },
                {
                  label: "Client Paid",
                  date: getDepositedPaymentDate() ||
                    (po.invoice?.sentDate
                      ? `Approx. ${moment(po.invoice.sentDate).add(po?.project?.client?.terms || 30, 'days').format("MMM D, YYYY")}`
                      : `Approx. ${po?.project?.client?.terms || 30} days after client is invoiced`),
                  isCompleted: ["client_paid", "paid"].includes(getCurrentPaymentStage()),
                  isActive: getCurrentPaymentStage() === "client_paid" && !isClientPaymentLate(),
                  isLate: isClientPaymentLate(),
                },
                {
                  label: "Paid",
                  date: getPaidTransactionDate(),
                  isCompleted: getCurrentPaymentStage() === "paid",
                  isActive: getCurrentPaymentStage() === "paid",
                },
              ]}
            />
          )}
          {/* Accepted/Denied status — show when PO is not status 1 and payment tracker is not showing */}
          {po.status !== 1 && !showPaymentTracker && (
            <Flex sx={{ alignItems: "center", justifyContent: "flex-end", gap: "20px" }}>
              <Flex
                sx={{
                  fontFamily: "Poppins",
                  fontSize: "12px",
                  alignItems: [2, 3, 5].includes(po.status) ? "center" : "flex-end",
                  fontWeight: "600",
                  height: "36px",
                  color: [2, 3, 5].includes(po.status) ? themevals.colors.success : themevals.colors.danger,
                }}
              >
                {[2, 3, 5].includes(po.status) && `Accepted by ${po.acceptedBy?.firstName} ${po.acceptedBy?.lastName} on ${moment(po.acceptedDate).format("MMM D, YYYY")}.`}
                {po.status === 4 && `Denied by ${po.deniedBy?.firstName} ${po.deniedBy?.lastName} on ${moment(po.deniedDate).format("MMM D, YYYY")}.`}
              </Flex>
              {[2, 3, 5].includes(po.status) && (
                <Flex
                  onClick={() => navigate(`/projects/${po.project.id}`)}
                  sx={{
                    fontFamily: "Poppins",
                    background: themevals.colors.success,
                    alignItems: "center",
                    padding: "0 15px",
                    borderRadius: "50px",
                    color: "white",
                    fontSize: "12px",
                    height: "36px",
                    cursor: "pointer",
                  }}
                >{`Project# GMP-${String(po.project.sequence).padStart(5, "0")}`}</Flex>
              )}
            </Flex>
          )}
        </Flex>
      </Flex>
      <Box
        sx={{
          maxWidth: "1000px",
          width: "100%",
          margin: "0 auto 60px auto",
          boxShadow: "0px 0px 12px 4px rgba(129, 129, 129, 0.07)",
          border: `1px solid ${themevals.colors.inputOutline}`,
          borderRadius: "6px",
          padding: "40px",
          backgroundColor: "#fff",
        }}
      >
        {/* 1. Header band — matches PDF top: logo+company left, TITLE/#/TOTAL right */}
        <Flex sx={{ justifyContent: "space-between", alignItems: "flex-start", gap: "20px", paddingBottom: "20px", borderBottom: "1px solid #000" }}>
          <Flex sx={{ flexDirection: "column", flex: 1 }}>
            {company?.data?.logo && (
              <img src={company.data.logo} alt="Company Logo" style={{ width: "auto", height: "40px", marginBottom: "12px", alignSelf: "flex-start" }} />
            )}
            <P fontFamily="Poppins" fontSize="12px" fontWeight="600" marginTop="0" marginBottom="2px">
              {company.name}
            </P>
            {company.data?.address?.line1 && (
              <P marginBottom="0px" marginTop="0px" fontFamily="Poppins" fontSize="11px" color={themevals.colors.dkgrey}>
                {company.data.address.line1}
              </P>
            )}
            {company.data?.address?.line2 && (
              <P marginBottom="0px" marginTop="0px" fontFamily="Poppins" fontSize="11px" color={themevals.colors.dkgrey}>
                {company.data.address.line2}
              </P>
            )}
            {(company.data?.address?.city || company.data?.address?.state || company.data?.address?.zip) && (
              <P marginBottom="0px" marginTop="0px" fontFamily="Poppins" fontSize="11px" color={themevals.colors.dkgrey}>
                {`${company.data?.address?.city ?? ""}${company.data?.address?.city ? ", " : ""}${company.data?.address?.state ?? ""} ${company.data?.address?.zip ?? ""}`.trim()}
              </P>
            )}
          </Flex>
          <Flex sx={{ flexDirection: "column", alignItems: "flex-end" }}>
            <Box sx={{ fontFamily: "Rubik-Medium, Rubik, sans-serif", fontSize: "22px", letterSpacing: "1px", color: "#000", textTransform: "uppercase" }}>
              Purchase Order
            </Box>
            <P marginBottom="0px" marginTop="2px" fontFamily="Poppins" fontSize="11px" fontWeight="600" color={themevals.colors.dkgrey}>
              {`#GMO-${String(po.sequence).padStart(5, "0")}`}
            </P>
            <Box sx={{ fontFamily: "Poppins-Medium, Poppins, sans-serif", fontSize: "8px", letterSpacing: "0.5px", color: themevals.colors.dkgrey, textTransform: "uppercase", marginTop: "14px" }}>
              Total
            </Box>
            <P marginBottom="0px" marginTop="2px" fontFamily="Poppins" fontSize="20px" fontWeight="500" color="#000">
              {po?.lineitems
                ?.flat()
                ?.reduce((acc: number, item: any) => acc + item.cost * item.quantity, 0)
                ?.toLocaleString("en-US", { style: "currency", currency: "USD" })}
            </P>
          </Flex>
        </Flex>

        {/* 2. Meta section — Provider address (left) + PO meta rows (right) */}
        <Grid gap={0} columns={[1, "1fr 1fr"]} sx={{ width: "100%", marginTop: "24px", paddingBottom: "16px", borderBottom: `1px solid ${themevals.colors.inputOutline}` }}>
          <Flex sx={{ flexDirection: "column" }}>
            <Box sx={{ fontFamily: "Poppins-Medium, Poppins, sans-serif", fontSize: "8px", letterSpacing: "0.5px", color: themevals.colors.dkgrey, textTransform: "uppercase", marginBottom: "4px" }}>
              Provider
            </Box>
            <P fontFamily="Poppins" fontSize="12px" fontWeight="600" marginTop="0" marginBottom="2px" color="#000">
              {providerInfo.name ? providerInfo.name : "-"}
            </P>
            {providerInfo.contactEmail && (
              <P marginBottom="0px" marginTop="0px" fontFamily="Poppins" fontSize="11px" color={themevals.colors.dkgrey}>
                {providerInfo.contactEmail}
              </P>
            )}
          </Flex>
          <Flex sx={{ flexDirection: "column", alignItems: "flex-end", gap: "8px" }}>
            <Flex sx={{ flexDirection: "column", alignItems: "flex-end" }}>
              <Box sx={{ fontFamily: "Poppins-Medium, Poppins, sans-serif", fontSize: "8px", letterSpacing: "0.5px", color: themevals.colors.dkgrey, textTransform: "uppercase" }}>
                PO Date
              </Box>
              <P marginBottom="0px" marginTop="1px" fontFamily="Poppins" fontSize="11px" fontWeight="500" color="#000">
                {po.createdAt ? moment(po.createdAt).format("MMM D, YYYY") : "-"}
              </P>
            </Flex>
            <Flex sx={{ flexDirection: "column", alignItems: "flex-end" }}>
              <Box sx={{ fontFamily: "Poppins-Medium, Poppins, sans-serif", fontSize: "8px", letterSpacing: "0.5px", color: themevals.colors.dkgrey, textTransform: "uppercase" }}>
                Estimated Service Date
              </Box>
              <P marginBottom="0px" marginTop="1px" fontFamily="Poppins" fontSize="11px" fontWeight="500" color="#000">
                {po.serviceDate ? formatDateOnly(po.serviceDate) : "-"}
              </P>
            </Flex>
            {po.quote?.sequence && (
              <Flex sx={{ flexDirection: "column", alignItems: "flex-end" }}>
                <Box sx={{ fontFamily: "Poppins-Medium, Poppins, sans-serif", fontSize: "8px", letterSpacing: "0.5px", color: themevals.colors.dkgrey, textTransform: "uppercase" }}>
                  Quote #
                </Box>
                <P marginBottom="0px" marginTop="1px" fontFamily="Poppins" fontSize="11px" fontWeight="500" color="#000">
                  {`GMQ-${String(po.quote.sequence).padStart(5, "0")}`}
                </P>
              </Flex>
            )}
            <Flex sx={{ flexDirection: "column", alignItems: "flex-end" }}>
              <Box sx={{ fontFamily: "Poppins-Medium, Poppins, sans-serif", fontSize: "8px", letterSpacing: "0.5px", color: themevals.colors.dkgrey, textTransform: "uppercase" }}>
                Terms
              </Box>
              <P marginBottom="0px" marginTop="1px" fontFamily="Poppins" fontSize="11px" fontWeight="500" color="#000">
                Net {po?.project?.client?.terms ? po.project.client.terms : 30}
              </P>
            </Flex>
          </Flex>
        </Grid>

        {/* 4. Line items table */}
        <Box sx={{ width: "100%", marginTop: "24px" }}>
          {items && <TableBasic data={items} columns={columns} enablePaging={false} enableSearch={false} customStyles={{ border: `1px solid ${themevals.colors.inputOutline}` }} disableSorting />}
          {/* Provider side of the fuel surcharge, after the quote was accepted.
              The backend refuses once the provider has billed this PO. */}
          {isCompany && (() => {
            const hasFuel = (items ?? []).some((i: any) => i?.type === FUEL_SURCHARGE_TYPE);
            const run = async () => {
              if (!fuelAction) return;
              const reason = fuelAction.reason.trim();
              if (!reason) return toast.add("Give a reason - it is saved on the project.", "var(--theme-ui-colors-warning)");
              setFuelAction({ ...fuelAction, busy: true });
              const r: any = await setPurchaseOrderSurcharge(po.id, fuelAction.mode, reason);
              if (r?.error) {
                setFuelAction({ ...fuelAction, busy: false });
                return toast.add(r.error, "var(--theme-ui-colors-danger)");
              }
              const mode = fuelAction.mode;
              setFuelAction(null);
              reloadPO(po);
              toast.add(mode === "refresh" ? "Fuel surcharge on this PO refreshed to the provider's current rate." : "Fuel surcharge removed from this PO.", "var(--theme-ui-colors-success)");
              // Zoho result from the save: say so when Zoho did not take it.
              if (r?.zohoSync && r.zohoSync.success === false) {
                toast.add(`Saved here, but Zoho Books was not updated: ${r.zohoSync.message || "unknown error"}`, "var(--theme-ui-colors-danger)");
              }
            };
            return (
              <Box sx={{ marginTop: "8px" }}>
                {!fuelAction && (
                  <Flex sx={{ justifyContent: "flex-end", gap: "8px" }}>
                    <BasicButton outline scheme="secondary" type="button" keepText onClick={() => setFuelAction({ mode: "refresh", reason: "", busy: false })}>
                      {hasFuel ? "Refresh fuel surcharge" : "Add fuel surcharge"}
                    </BasicButton>
                    {hasFuel && (
                      <BasicButton outline scheme="danger" type="button" keepText onClick={() => setFuelAction({ mode: "remove", reason: "", busy: false })}>Remove fuel surcharge</BasicButton>
                    )}
                  </Flex>
                )}
                {fuelAction && (
                  <Box sx={{ border: `1px solid ${themevals.colors.inputOutline}`, borderRadius: "6px", padding: "12px", background: "#FAFAF7" }}>
                    <div style={{ fontFamily: "Poppins", fontSize: 13, fontWeight: 600, marginBottom: 4 }}>
                      {fuelAction.mode === "refresh" ? "Set the fuel surcharge to the provider's current rate" : "Remove the fuel surcharge from this PO"}
                    </div>
                    <div style={{ fontFamily: "Poppins", fontSize: 12, color: themevals.colors.dkgrey, marginBottom: 6 }}>
                      {fuelAction.mode === "refresh"
                        ? "Uses the provider's rate in Settings > Fuel Surcharge and this PO's costs. The client's quote and invoice are not changed."
                        : "Only if the provider has agreed not to charge it. The client's quote and invoice are not changed."}
                    </div>
                    <textarea
                      value={fuelAction.reason}
                      onChange={(e) => setFuelAction({ ...fuelAction, reason: e.target.value })}
                      placeholder="Reason (required) - saved on the project"
                      rows={2}
                      style={{ width: "100%", boxSizing: "border-box", border: "1px solid #EBEBEB", borderRadius: 6, padding: "7px 10px", fontFamily: "Poppins", fontSize: 13 }}
                    />
                    <Flex sx={{ justifyContent: "flex-end", gap: "8px", marginTop: "10px" }}>
                      <BasicButton outline scheme="secondary" type="button" keepText disabled={fuelAction.busy} onClick={() => setFuelAction(null)}>Cancel</BasicButton>
                      <BasicButton type="button" keepText disabled={fuelAction.busy} onClick={run}>
                        {fuelAction.busy ? "Saving\u2026" : fuelAction.mode === "refresh" ? "Refresh" : "Remove"}
                      </BasicButton>
                    </Flex>
                  </Box>
                )}
              </Box>
            );
          })()}
        </Box>

        {/* 5. Totals block — right-aligned with rule above final Total */}
        <Flex sx={{ justifyContent: "flex-end", width: "100%", marginTop: "24px" }}>
          <Box sx={{ width: "260px" }}>
            <Flex sx={{ justifyContent: "space-between", paddingBottom: "8px" }}>
              <P fontFamily="Poppins" fontSize="11px" marginTop="0" marginBottom="0" color={themevals.colors.dkgrey}>Subtotal</P>
              <P fontFamily="Poppins" fontSize="11px" marginTop="0" marginBottom="0" fontWeight="500" color="#000">
                {po?.lineitems?.flat()?.reduce((acc: number, item: any) => acc + item.cost * item.quantity, 0)?.toLocaleString("en-US", { style: "currency", currency: "USD" })}
              </P>
            </Flex>
            <Box sx={{ borderTop: "1px solid #000", paddingTop: "8px" }}>
              <Flex sx={{ justifyContent: "space-between", alignItems: "baseline" }}>
                <P fontFamily="Poppins" fontSize="12px" fontWeight="600" marginTop="0" marginBottom="0" color="#000">Total</P>
                <P fontFamily="Poppins" fontSize="14px" fontWeight="600" marginTop="0" marginBottom="0" color="#000">
                  {po?.lineitems?.flat()?.reduce((acc: number, item: any) => acc + item.cost * item.quantity, 0)?.toLocaleString("en-US", { style: "currency", currency: "USD" })}
                </P>
              </Flex>
            </Box>
          </Box>
        </Flex>

        {/* 5b. Project Details block. Renders when there is a site
            address or a supply method. Site is the prominent first row;
            for install-only jobs the supply rows (method, material
            location) appear below. Service type and product are NOT
            repeated here - they are already in the line items table
            above. Positioned below the line items, above Terms. Generic
            "Supplier"; never the client identity. */}
        {(po?.project?.location?.address || po?.supplyMethod) && (() => {
          const method = po?.supplyMethod as string | null;

          // Site address (prominent, always shown when present).
          const site = po?.project?.location?.address || {};
          const siteLine = [site.line1, `${site.city ?? ""}, ${site.state ?? ""} ${site.postalCode ?? ""}`.trim()].filter(Boolean).join(" \u2014 ");

          // Material source address: the operator-set supplySourceAddress wins;
          // otherwise fall back to the resolved depot address on the line item.
          const src = po?.supplySourceAddress || {};
          const depotAddr = (Array.isArray(po?.lineitems) ? po.lineitems.flat().find((li: any) => li?.depot?.address)?.depot?.address : null) || {};
          const sourceAddr = src.line1 ? src : depotAddr;
          const sourceLine = [sourceAddr.line1, sourceAddr.city, sourceAddr.state, sourceAddr.postalCode].filter(Boolean).join(", ");

          // A provider does not need to be told their OWN address. For
          // delivery_to_yard, if the yard matches the provider's own registered
          // address or the resolved depot address, suppress it for a provider
          // viewer (they know it) and show a note instead. Internal team and the
          // client still see it. Pickup is always shown (a place to drive to).
          const norm = (a: any) =>
            [a?.line1, a?.city, a?.state, a?.postalCode].filter(Boolean).join("|").toLowerCase().replace(/\s+/g, " ").trim();
          const providerOwnAddr = po?.provider?.address || {};
          const depotOwnAddr = (Array.isArray(po?.lineitems) ? po.lineitems.flat().find((li: any) => li?.depot?.address)?.depot?.address : null) || {};
          const srcKey = norm(sourceAddr);
          const matchesProviderOwn = !!srcKey && (srcKey === norm(providerOwnAddr) || srcKey === norm(depotOwnAddr));
          const isProviderViewer = user?.accessLevel === "isProvider";
          const hideYardAddrForProvider = method === "delivery_to_yard" && isProviderViewer && matchesProviderOwn;

          const methodLabel =
            method === "pickup_from_fibar" ? "Pickup from supplier"
            : method === "delivery_to_site" ? "Supplier delivers to the site"
            : method === "delivery_to_yard" ? "Supplier delivers to your yard"
            : null;
          // Material location shown only when it differs from the site
          // (pickup depot, installer yard). delivery_to_site comes to the
          // site, so no repeated address - a note references the Site above.
          const locLabel =
            method === "pickup_from_fibar" ? "Pickup location"
            : method === "delivery_to_yard" ? "Yard location"
            : null;
          const microLabel = { fontFamily: "Poppins-Medium, Poppins, sans-serif", fontSize: "9px", letterSpacing: "0.5px", color: themevals.colors.dkgrey, textTransform: "uppercase", marginBottom: "4px" } as const;
          const hasSupply = !!method;
          return (
            <Box sx={{ width: "100%", marginTop: "24px", border: `1px solid ${themevals.colors.inputOutline}`, borderRadius: "6px", padding: "16px 18px", background: themevals.colors.base_100 }}>
              <Box sx={{ fontFamily: "Poppins", fontSize: "13px", fontWeight: 600, color: "#000", marginBottom: "12px" }}>Project Details</Box>
              {siteLine && (
                <Box>
                  <Box sx={microLabel}>Site</Box>
                  <P marginBottom="0px" marginTop="0px" fontFamily="Poppins" fontSize="14px" fontWeight="600" color="#000">{siteLine}</P>
                </Box>
              )}
              {hasSupply && (
                <Box sx={{ marginTop: siteLine ? "12px" : "0px", paddingTop: siteLine ? "12px" : "0px", borderTop: siteLine ? `1px solid ${themevals.colors.inputOutline}` : "none" }}>
                  <Flex sx={{ gap: "30px", flexWrap: "wrap" }}>
                    {methodLabel && (
                      <Box>
                        <Box sx={microLabel}>Supply Method</Box>
                        <P marginBottom="0px" marginTop="0px" fontFamily="Poppins" fontSize="13px" fontWeight="600" color="#000">{methodLabel}</P>
                      </Box>
                    )}
                  </Flex>
                  {locLabel && sourceLine && !hideYardAddrForProvider && (
                    <Box sx={{ marginTop: "12px" }}>
                      <Box sx={microLabel}>{locLabel}</Box>
                      <P marginBottom="0px" marginTop="0px" fontFamily="Poppins" fontSize="13px" fontWeight="600" color="#000">{sourceLine}</P>
                    </Box>
                  )}
                  {hideYardAddrForProvider && (
                    <Box sx={{ marginTop: "12px" }}>
                      <P marginBottom="0px" marginTop="0px" fontFamily="Poppins" fontSize="12px" fontWeight="500" color={themevals.colors.dkgrey}>
                        Material is delivered to your yard.
                      </P>
                    </Box>
                  )}
                  {method === "delivery_to_site" && (
                    <Box sx={{ marginTop: "12px" }}>
                      <P marginBottom="0px" marginTop="0px" fontFamily="Poppins" fontSize="12px" fontWeight="500" color={themevals.colors.dkgrey}>
                        Material is delivered to the site above.{po?.onSiteLoadingRequired ? " Loading equipment required on site." : ""}
                      </P>
                    </Box>
                  )}
                </Box>
              )}
            </Box>
          );
        })()}

        {/* 6. Terms footnote */}
        <Box sx={{ width: "100%", marginTop: "40px", paddingTop: "20px", borderTop: `1px solid ${themevals.colors.inputOutline}` }}>
          {isCompany ? (
            /* Staff: editable per-record T&C override (Session 59, Part B).
               Provider view keeps the read-only rendered terms below. */
            <TermsEditor
              docType="po"
              record={po}
              update={updatePurchaseOrder}
              onSaved={() => reloadPO(po)}
            />
          ) : (
            <>
              <Box sx={{ fontFamily: "Poppins-Medium, Poppins, sans-serif", fontSize: "8px", letterSpacing: "0.5px", color: themevals.colors.dkgrey, textTransform: "uppercase", marginBottom: "6px" }}>
                Terms
              </Box>
              <P color={themevals.colors.dkgrey} marginBottom="0" marginTop="0" fontFamily="Poppins" fontSize="10px" lineHeight="16px" style={{ whiteSpace: "pre-wrap" }}>
                {po?.resolvedTerms || ""}
              </P>
            </>
          )}
        </Box>
      </Box>
      {confirmDenyModalOpen && (
        <BasicModal
          title="Deny PO?"
          cancel_click={() => cancelDenyConfirm()}
          cancel_text="No, cancel"
          $submitting={submitting}
          size={"sm"}
          icon={<QuestionMarkIcon fill={"#818181"} height="30px" />}
          actions={
            <BasicButton
              type="button"
              scheme="danger"
              onClick={() => submitDeny()}
              $submitting={submitting}
              $iconHeight="14px"
              $iconWidth="14px"
              $loadingColor="#fff"
              keepText={true}
              styles={{ margin: "0 0.625rem .25rem 0" }}
            >
              {!submitting && <TimesIcon fill={"#fff"} />}
              {"Deny PO"}
            </BasicButton>
          }
        >
          <P lineHeight={"22px"}>Are you sure you want to deny this PO?</P>
          <Box sx={{ marginTop: "20px" }}>
            <P fontFamily="Poppins" fontSize="12px" fontWeight="600" marginBottom="8px" marginTop="0" textAlign="left">
              Reason for denial <Box as="span" sx={{ fontWeight: 500 }}>(optional)</Box>
            </P>
            <textarea
              value={denyNote}
              onChange={(e) => setDenyNote(e.target.value)}
              rows={4}
              style={{
                width: "100%",
                padding: "12px",
                fontFamily: "Poppins",
                fontSize: "14px",
                border: `1px solid ${themevals.colors.inputOutline}`,
                borderRadius: "6px",
                resize: "none" as const,
              }}
            />
          </Box>
        </BasicModal>
      )}
      {confirmAcceptModalOpen && (
        <BasicModal
          title="Accept PO?"
          cancel_click={() => cancelAcceptConfirm()}
          cancel_text="No, cancel"
          $submitting={submitting}
          size={"sm"}
          icon={<QuestionMarkIcon fill={"#818181"} height="30px" />}
          actions={
            <BasicButton
              type="button"
              scheme="primary"
              onClick={() => handleAcceptConfirm()}
              $submitting={submitting}
              $iconHeight="14px"
              $iconWidth="14px"
              $loadingColor="#fff"
              keepText={true}
              styles={{ margin: "0 0.625rem .25rem 0" }}
            >
              {!submitting && <CheckMarkIcon fill={"#fff"} />}
              {"Accept PO"}
            </BasicButton>
          }
        >
          <P lineHeight={"22px"}>Are you sure you want to accept this PO?</P>
        </BasicModal>
      )}
      {acceptNoteModalOpen && (
        <BasicModal
          cancel_click={() => handleCancelAcceptNote()}
          cancel_text="Skip"
          $submitting={submitting}
          size={"sm"}
          actions={
            <BasicButton
              type="button"
              scheme="primary"
              onClick={() => handleSaveAcceptNote()}
              $submitting={submitting}
              $iconHeight="14px"
              $iconWidth="14px"
              $loadingColor="#fff"
              keepText={true}
              styles={{ margin: "0 0.625rem .25rem 0" }}
            >
              {"Save"}
            </BasicButton>
          }
        >
          <Box>
            <Flex sx={{ alignItems: "center", justifyContent: "center", gap: "8px", marginBottom: "20px" }}>
              <Box
                sx={{
                  width: "20px",
                  height: "20px",
                  borderRadius: "50%",
                  backgroundColor: themevals.colors.success,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                <CheckMarkIcon fill="#fff" width="12px" height="12px" />
              </Box>
              <P 
                fontFamily="Rubik" 
                fontSize="16px" 
                fontWeight="600" 
                marginBottom="0" 
                marginTop="0"
                color={themevals.colors.success}
              >
                PO Accepted
              </P>
            </Flex>
            <P fontFamily="Poppins" fontSize="14px" marginBottom="20px" marginTop="0" color={themevals.colors.dkgrey}>
              Leave a note with any additional comments.
            </P>
            <P fontFamily="Poppins" fontSize="12px" fontWeight="600" marginBottom="8px" marginTop="0" textAlign="left">
              Note <Box as="span" sx={{ fontWeight: 500 }}>(optional)</Box>
            </P>
            <textarea
              value={acceptNote}
              onChange={(e) => setAcceptNote(e.target.value)}
              rows={4}
              style={{
                width: "100%",
                padding: "12px",
                fontFamily: "Poppins",
                fontSize: "14px",
                border: `1px solid ${themevals.colors.inputOutline}`,
                borderRadius: "6px",
                resize: "none" as const,
              }}
            />
          </Box>
        </BasicModal>
      )}
    </>
  );
};

export default Overview;
